<template>
  <nx-dashboard-card class="nx-dashboard-stored-searches-card"
                     :title="getSearcherGroupTitle(savedSearchesEntity)"
                     :hide-actions="!hasSearches"
  >
    <template v-slot:content>
      <v-list>
        <nx-dashboard-menu-list-item
          v-for="(searches, entity) in groupedSearches"
          :key="entity"
          :selected="entity === savedSearchesEntity"
          :text="getSearcherGroupTitle(entity)"
          @click="setCurrentSearcherEntity(entity)"/>
      </v-list>
    </template>
    <template v-slot:body>
      <v-layout v-if="!hasSearches">
        <v-flex>
          <div class="text-xs-center pa-4">
            <div class="subheading mb-4">Personaliza tus búsquedas</div>
            <div class="subheading primary--text">
              Aún no tienes ninguna búsqueda guardada.
              <a href="#" @click.prevent="openDialog('savedSearchesWizard')">Crea una nueva búsqueda</a>
              a partir del buscador y guárdala para que te aparezca siempre accesible aquí.
            </div>
          </div>
        </v-flex>

        <nx-saved-searches-wizard
          v-if="dialogState.savedSearchesWizard.render"
          :value="dialogState.savedSearchesWizard.active"
          entity="content"
          entity-tabs
          @input:dialog="closeDialog('savedSearchesWizard')"
        />
      </v-layout>

      <v-layout class="nx-list row wrap" v-else>

        <v-list-tile class="nx-list-item" v-if="searchesInSelectedGroup.length === 0" subheader>
          <v-list-tile-avatar>
            <v-icon class="nx-list-item-icon">search</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <span class="nx-list-item-link">{{ $t('message.dashboard.searcher.empty') }}</span>
          </v-list-tile-content>
        </v-list-tile>

        <v-list-tile class="nx-list-item"
                     v-else
                     v-for="search in searchesInSelectedGroup"
                     :key="search.uuid"
                     :to="{ name: 'searcher', params: { entity: search.searcher }, query: search.filters  }"
                     subheader>
          <v-list-tile-avatar>
            <v-icon class="nx-list-item-icon">search</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <span class="nx-list-item-link">{{ search.name }}</span>
          </v-list-tile-content>
        </v-list-tile>
      </v-layout>
    </template>
    <template v-slot:footer>
      <nx-dashboard-card-footer
        :title="$t('message.searcher.savedSearch.editSearches')"
        @click="openDialog('savedSearchesManager')"
      />
      <nx-saved-searches-manage-dialog
        v-if="dialogState.savedSearchesManager.render"
        :value="dialogState.savedSearchesManager.active"
        @input:dialog="closeDialog('savedSearchesManager')"
      />
    </template>
  </nx-dashboard-card>
</template>

<script>
import isEmpty from 'lodash/isEmpty';
import NxDashboardCard from '@/modules/dashboard/components/DashboardCard';
import DashboardCard from '@/modules/dashboard/mixins/dashboard-card';
import NxDashboardMenuListItem from '@/modules/dashboard/components/DashboardMenuListItem';
import NxDashboardCardFooter from '@/modules/dashboard/components/DashboardCardFooter';
import NxSavedSearchesManageDialog from '@/modules/saved-searches/components/SavedSearchesManageDialog.vue';
import ManageDialogs, { initDialog } from '@/mixins/manage-dialogs';
import ListSavedSearches from '@/modules/saved-searches/mixins/list-saved-searches';
import NxSavedSearchesWizard from '@/modules/saved-searches/components/SavedSearchesWizard.vue';
import { mapGetters } from 'vuex';

const CONTENT_SEARCHER = 'content';

export default {
  name: 'nx-dashboard-stored-searches-card',
  mixins: [DashboardCard, ManageDialogs, ListSavedSearches],
  components: {
    NxSavedSearchesWizard,
    NxSavedSearchesManageDialog,
    NxDashboardCardFooter,
    NxDashboardMenuListItem,
    NxDashboardCard,
  },
  data() {
    return {
      dialogState: {
        savedSearchesManager: initDialog(),
        savedSearchesWizard: initDialog(),
      },
      savedSearchesEntity: null,
    };
  },
  computed: {
    ...mapGetters('dashboard', ['getCardStaffPreferences']),
    searchesInSelectedGroup() {
      return this.groupedSearches[this.savedSearchesEntity] || [];
    },
  },
  methods: {
    getSearcherGroupTitle(searcher) {
      if (!searcher) return this.$t('message.dashboard.searcher.title');

      const translation = this.$tc(`message.searcher.tab.${searcher}`);
      return this.$t('message.dashboard.searcher.list', { entity: translation });
    },
    getDefaultSearcherGroup() {
      if (isEmpty(this.groupedSearches)) {
        return null;
      }
      // Si existe contenido, este será el primer searcher
      if (!isEmpty(this.groupedSearches[CONTENT_SEARCHER])) {
        return CONTENT_SEARCHER;
      }

      // Devolvemos el searcher del primer elemento
      return Object.keys(this.groupedSearches)[0] || null;
    },
    setCurrentSearcherEntity(entity) {
      this.savedSearchesEntity = entity;
      this.saveCurrentSearcherEntity();
    },
    saveCurrentSearcherEntity() {
      this.$store.dispatch('dashboard/SAVE_STORED_SEARCHES_CURRENT_ENTITY', {
        cardId: this.id,
        savedSearchesEntity: this.savedSearchesEntity,
      });
    },
  },
  async mounted() {
    // Get the last entity selected for the staff
    if (!this.safeStaffPreferences.savedSearchesEntity
      || isEmpty(this.groupedSearches[this.safeStaffPreferences.savedSearchesEntity])
    ) {
      // If there is no entity saved, or that entity does not have any search, use the default one.
      this.savedSearchesEntity = this.getDefaultSearcherGroup();
    } else {
      this.savedSearchesEntity = this.safeStaffPreferences.savedSearchesEntity;
    }
  },
  watch: {
    groupedSearches() {
      if (!this.savedSearchesEntity
        || isEmpty(this.groupedSearches[this.savedSearchesEntity])
      ) {
        this.savedSearchesEntity = this.getDefaultSearcherGroup();
      }
    },
  },
};
</script>

<style lang="stylus">
@import '../../../assets/stylus/variables.styl'

.nx-dashboard-stored-searches-card {
  .nx-list {
    margin: 0 -16px;

    .nx-list-item {
      height: 40px;
      border-bottom: 1px solid $nx-neutral-100;

      .v-list__tile {
        height: 40px;
        padding: 0 8px;
      }

      .v-list__tile__avatar {
        min-width: 48px;
      }

      &-icon {
        color: $nx-neutral-200;
        font-size: 24px;
      }

      &-link {
        text-decoration: none;
        color: $nx-neutral-800;
        font-size: 15px;
        font-weight: 400;
        line-height: 130%;
      }
    }
  }
}
</style>
